import React, { useState, useEffect, useRef } from 'react';

/* CSS keyframes injected once */
const styleId = 'anim-select-styles';
if (typeof document !== 'undefined' && !document.getElementById(styleId)) {
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
@keyframes anim-dropdown-in {
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes anim-dropdown-out {
  from { opacity: 1; transform: translateY(0); }
  to   { opacity: 0; transform: translateY(-4px); }
}
.anim-select-list::-webkit-scrollbar { width: 4px; }
.anim-select-list::-webkit-scrollbar-track { background: transparent; }
.anim-select-list::-webkit-scrollbar-thumb { background: rgba(59,130,246,0.2); border-radius: 4px; }
`;
  document.head.appendChild(style);
}

export default function AnimSelect({ value, label, options, onChange, hasError, placeholder }) {
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const ref = useRef(null);
  const openRef = useRef(false);

  useEffect(() => { openRef.current = open; }, [open]);

  const displayLabel = label || placeholder || 'Select...';

  const handleClose = () => {
    if (!openRef.current) return;
    setClosing(true);
    setTimeout(() => { setOpen(false); setClosing(false); }, 150);
  };

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) handleClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (val) => {
    onChange(val);
    handleClose();
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div
        onClick={() => open ? handleClose() : setOpen(true)}
        style={{
          background: 'var(--bg-input, #0e0e15)',
          border: hasError ? '1px solid var(--accent-red, #ef4444)' : open ? '1px solid var(--border-hover, rgba(255,255,255,0.14))' : '1px solid var(--border-subtle, rgba(255,255,255,0.06))',
          borderRadius: 'var(--radius-md, 8px)',
          color: value ? 'var(--text-primary, #e8e8ed)' : 'var(--text-muted, #4a4a5a)',
          padding: '0.5rem 2rem 0.5rem 0.85rem',
          fontSize: '0.85rem',
          fontFamily: 'var(--font-sans)',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          userSelect: 'none',
          position: 'relative',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {displayLabel}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted, #4a4a5a)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          style={{
            position: 'absolute', right: 10, top: '50%',
            transform: `translateY(-50%) rotate(${open ? 180 : 0}deg)`,
            transition: 'transform 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>
      {open && (
        <div className="anim-select-list" style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 999,
          background: 'var(--bg-elevated, #1a1a24)',
          border: '1px solid var(--border-default, rgba(255,255,255,0.09))',
          borderRadius: 'var(--radius-md, 8px)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          padding: '4px',
          animation: closing ? 'anim-dropdown-out 0.15s ease forwards' : 'anim-dropdown-in 0.15s ease forwards',
          maxHeight: 340,
          overflowY: 'auto',
        }}>
          {options.map((opt, idx) => (
            <div
              key={idx}
              onClick={() => !opt.disabled && handleSelect(opt.value)}
              style={{
                padding: '0.45rem 0.75rem',
                borderRadius: 'var(--radius-sm, 6px)',
                fontSize: '0.82rem',
                fontFamily: 'var(--font-sans)',
                cursor: opt.disabled ? 'not-allowed' : 'pointer',
                color: opt.disabled ? 'var(--text-muted, #4a4a5a)' : opt.value === value ? 'var(--accent-blue, #3b82f6)' : 'var(--text-secondary, #8b8b9e)',
                background: opt.value === value ? 'rgba(59,130,246,0.08)' : 'transparent',
                fontWeight: opt.value === value ? 600 : 400,
                transition: 'all 0.12s ease',
                opacity: opt.disabled ? 0.4 : 1,
                display: 'flex', alignItems: 'center', gap: '0.4rem',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}
              onMouseOver={e => { if (!opt.disabled) { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.color = 'var(--text-primary, #e8e8ed)'; } }}
              onMouseOut={e => { e.currentTarget.style.background = opt.value === value ? 'rgba(59,130,246,0.08)' : 'transparent'; e.currentTarget.style.color = opt.disabled ? 'var(--text-muted, #4a4a5a)' : opt.value === value ? 'var(--accent-blue, #3b82f6)' : 'var(--text-secondary, #8b8b9e)'; }}
            >
              {opt.value === value && <span style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--accent-blue, #3b82f6)', flexShrink: 0 }} />}
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
